import type { ZodObject, ZodRawShape, ZodType } from 'zod';

import type { CountryCode } from '@apple/utils/globalization/models';
import type { AllKeysAreStrings } from '@apple/utils/type';
import { useValidationSchema } from '@apple/utils/validation/hooks/useValidationSchema';
import type { ValidationType } from '@apple/utils/validation/models';

import type { ValidatedFormProps } from './ValidatedForm';
import { ValidatedZodForm } from './ValidatedZodForm';

type Props<
	T extends AllKeysAreStrings<T>,
	TSchema extends ZodObject<ZodRawShape>,
	TTransformedValues = T,
> = Omit<ValidatedFormProps<T, TTransformedValues>, 'validate'> & {
	validationType: ValidationType;
	countryCode?: CountryCode;
	schema: TSchema;
	schemaExtension?: (schema: TSchema) => ZodType;
};

export function ServerValidatedZodForm<
	T extends AllKeysAreStrings<T>,
	TSchema extends ZodObject<ZodRawShape>,
	TTransformedValues = T,
>({
	validationType,
	countryCode,
	schema,
	schemaExtension,
	...rest
}: Props<T, TSchema, TTransformedValues>) {
	const { schema: schemaWithServerRules } = useValidationSchema({
		validationType,
		countryCode,
		existingSchema: schema,
	});

	return (
		<ValidatedZodForm<T, TSchema, TTransformedValues>
			{...rest}
			schema={schemaWithServerRules}
			schemaExtension={schemaExtension}
		/>
	);
}
